import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { mappingsApi } from '../api/mappings'
import type { SavedMapping } from '../api/types'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatInt, formatRelative } from '../lib/format'

export function MappingsPage() {
  const mappings = useAsync<SavedMapping[]>(() => mappingsApi.list(), [])
  const [entity, setEntity] = useState('')
  const [search, setSearch] = useState('')
  const [removing, setRemoving] = useState<string | null>(null)

  const all = useMemo(() => mappings.data ?? [], [mappings.data])
  const entities = useMemo(
    () => Array.from(new Set(all.map((mapping) => mapping.entityType))).sort(),
    [all],
  )

  const rows = all.filter(
    (mapping) =>
      (!entity || mapping.entityType === entity) &&
      (!search || mapping.name.toLowerCase().includes(search.toLowerCase())),
  )

  const remove = async (id: string) => {
    setRemoving(id)
    try {
      await mappingsApi.remove(id)
      mappings.reload()
    } finally {
      setRemoving(null)
    }
  }

  return (
    <div className="wrap">
      <div className="phead">
        <div>
          <div className="crumb">
            <Link to="/app">Home</Link> / Saved mappings
          </div>
          <h2>Saved mappings</h2>
          <p className="fs13 muted" style={{ margin: '6px 0 0' }}>
            A mapping remembers which column in your file feeds which QuickBooks field, so recurring
            files skip step 2 entirely.
          </p>
        </div>
        <span className="spacer" />
        <Link className="btn btn-p" to="/app/import">
          + New mapping
        </Link>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>{formatInt(all.length)} mappings</h3>
          <span className="spacer" />
          <select
            className="inp"
            style={{ width: 170, height: 32 }}
            value={entity}
            onChange={(event) => setEntity(event.target.value)}
          >
            <option value="">All entities</option>
            {entities.map((name) => (
              <option value={name} key={name}>
                {name}
              </option>
            ))}
          </select>
          <input
            className="inp"
            style={{ width: 200, height: 32 }}
            placeholder="Search mappings"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {mappings.error ? (
          <ErrorState error={mappings.error} onRetry={mappings.reload} />
        ) : mappings.loading && !mappings.data ? (
          <LoadingState label="Loading mappings…" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="⇄"
            title={all.length === 0 ? 'No saved mappings yet' : 'Nothing matches that filter'}
            body={
              all.length === 0
                ? 'Save a mapping at the end of step 2 of an import and it will be reusable here.'
                : undefined
            }
            action={
              all.length === 0 ? (
                <Link className="btn btn-p" to="/app/import">
                  Start an import
                </Link>
              ) : undefined
            }
          />
        ) : (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Entity</th>
                  <th className="n">Fields</th>
                  <th>Scope</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((mapping) => (
                  <tr className="hov" key={mapping.id}>
                    <td className="fs13">
                      <b>{mapping.name}</b>
                    </td>
                    <td className="fs13">{mapping.entityType}</td>
                    <td className="n mono fs13">
                      {formatInt(
                        mapping.definition.columns.filter((column) => column.sourceColumn).length,
                      )}
                    </td>
                    <td className="fs13">{mapping.companyName ?? 'All companies'}</td>
                    <td className="fs13 mono">
                      {mapping.lastUsedAt ? formatRelative(mapping.lastUsedAt) : '—'}
                    </td>
                    <td>
                      <button
                        className="btn btn-g btn-sm"
                        type="button"
                        disabled={removing === mapping.id}
                        onClick={() => void remove(mapping.id)}
                      >
                        {removing === mapping.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card-f">
          <span className="fs13 muted">
            Showing {formatInt(rows.length)} of {formatInt(all.length)}
          </span>
        </div>
      </div>
    </div>
  )
}
